"use client";

import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../components/organisms/AdminHeader";
import SectionBox from "../../components/layout/SectionBox";
import Footer from "../../components/organisms/Footer";
import Button from "../../components/atoms/Button";
import TiptapEditor from "../../components/atoms/TiptapEditor";
import { TagSelectInput } from "../../components/molecules/TagSelectInput";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { useJobPostStore } from "../../store/useJobPostStore";

export default function AdminJobPostEdit() {
    const { id } = useParams(); // <- job ID dari URL
    const location = useLocation();
    const lang = location.pathname.startsWith("/id") ? "id" : "en";

    // STATES
    const [jobTitleEN, setJobTitleEN] = useState("");
    const [jobTitleID, setJobTitleID] = useState("");
    const [jobType, setJobType] = useState("");
    const [industry, setIndustry] = useState("");
    const [workingType, setWorkingType] = useState("");
    const [degree, setDegree] = useState("");
    const [experience, setExperience] = useState("");
    const [experienceLevel, setExperienceLevel] = useState("");
    const [locationField, setLocationField] = useState("");

    const [minSalary, setMinSalary] = useState<number | string>("");
    const [maxSalary, setMaxSalary] = useState<number | string>("");
    const [notDisclose, setNotDisclose] = useState(false);

    const [benefits, setBenefits] = useState<string[]>([]);
    const [skills, setSkills] = useState<string[]>([]);

    const [responsibilitiesEN, setResponsibilitiesEN] = useState("");
    const [responsibilitiesID, setResponsibilitiesID] = useState("");
    const [requirementsEN, setRequirementsEN] = useState("");
    const [requirementsID, setRequirementsID] = useState("");

    const [activeTab, setActiveTab] = useState<"en" | "id">("en");

    // tambahan di useState
    const [countryId, setCountryId] = useState("");
    const [stateId, setStateId] = useState("");
    const [cityId, setCityId] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ambil store tambahan
    const { countries, states, cities, fetchStates, fetchCities } = useJobFilterStore();

    const formatCurrency = (value: number | string) => {
        if (value === "" || value === null) return "";
        return new Intl.NumberFormat("id-ID").format(Number(value));
    };

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numeric = e.target.value.replace(/\D/g, ""); // hapus semua non-digit
        setMinSalary(numeric ? Number(numeric) : "");
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numeric = e.target.value.replace(/\D/g, "");
        setMaxSalary(numeric ? Number(numeric) : "");
    };

    // fetch states ketika country berubah
    useEffect(() => {
        if (!countryId) {
            setStateId("");
            setCityId("");
            return;
        }
        fetchStates(Number(countryId), lang);
        setStateId("");
        setCityId("");
    }, [countryId, fetchStates, lang]);

    // fetch cities ketika state berubah
    useEffect(() => {
        if (!stateId) {
            setCityId("");
            return;
        }
        fetchCities(Number(stateId), lang);
        setCityId("");
    }, [stateId, fetchCities, lang]);

    // STORE FILTER OPTIONS
    const { jobTypes, degrees, experiences, experienceLevels, industries, locations, skillSet, workingTypes, benefitSet, fetchFilters } = useJobFilterStore();
    const skillOptions = skillSet?.map((item) => ({
        id: item.id,
        name: item.name
    })) ?? [];
    const benefitOptions = benefitSet?.map((item) => ({
        id: item.id,
        name: item.name
    })) ?? [];

    useEffect(() => {
        fetchFilters(lang);
    }, [lang, fetchFilters]);

    useEffect(() => {
        console.log("jobTypes updated:", jobTypes);
    }, [jobTypes]);

    const handleToggleUndisclosed = () => {
        const next = !notDisclose;
        setNotDisclose(next);
        if (next) {
            // setMinSalary(0);
            // setMaxSalary(0);
        }
    };

    const { submitJobPost } = useJobPostStore();
    const navigate = useNavigate();
    
    const finalJobTitleID = jobTitleID?.trim() || jobTitleEN;

    const isFormValid =
        // Job title
        jobTitleEN?.trim() &&

        // Responsibilities
        responsibilitiesEN?.trim() &&

        // Requirements
        requirementsEN?.trim() &&

        // Dropdowns
        workingType &&
        jobType &&
        experienceLevel &&
        experience &&
        degree &&
        industry &&
        countryId &&
        stateId &&
        cityId &&

        // Arrays
        skills.length > 0 &&
        benefits.length > 0;

    const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) {
        if (!isFormValid) {
            toast.error("Please complete all required fields");
        }
        return;
    }

    setIsSubmitting(true);

    try {
        const payload = {
            name: {
                en: jobTitleEN,
                id: finalJobTitleID,
            },
            responsibility: {
                en: responsibilitiesEN,
                id: responsibilitiesID?.trim() || responsibilitiesEN,
            },
            requirement: {
                en: requirementsEN,
                id: requirementsID?.trim() || requirementsEN,
            },
            working_type_id: Number(workingType),
            job_type_id: Number(jobType),
            experience_level_id: Number(experienceLevel),
            experience_year_id: Number(experience),
            degree_id: Number(degree),
            industry_id: Number(industry),
            country_id: Number(countryId),
            state_id: Number(stateId),
            city_id: Number(cityId),
            min_salary: minSalary === "" ? null : Number(minSalary),
            max_salary: maxSalary === "" ? null : Number(maxSalary),
            show_salary: notDisclose,
            status: "draft",
            skills: skills.map(Number),
            benefits: benefits.map(Number),
        };

        console.log("Final Payload", payload);

        const result = await submitJobPost(payload);

        if (result.success) {
            toast.success(result.message || "Job Post successfully");
            navigate(`/${lang}/admin/job-post`);
        } else {
            toast.error(result.message || "Job Post Failed");
        }

    } catch (error: any) {
        console.error(error);

        // kalau pakai axios biasanya message ada di sini
        const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong";

        toast.error(errorMessage);
    } finally {
        setIsSubmitting(false);
    }
};






    return (
        <>
            <AdminHeader />
            <main>
                <div className="container-section-jobs mx-auto px-6 pt-6 pb-6">
                    <SectionBox className="mt-24">

                        <h2 className="text-2xl font-semibold text-center mb-6 text-white">
                            Create Job Posting
                        </h2>

                        {/* BASIC INFO */}
                        <div className="space-y-2">
                            <p className="font-medium mb-6">Basic Information</p>
                            <div className="mt-6">
                                <div className="flex">
                                    <div className="flex border-b mb-2">
                                        <button className={`pe-4 pb-2 font-medium ${activeTab === "en" ? "border-b-2 border-white text-white" : "text-black"}`} onClick={() => setActiveTab("en")}>
                                            Job Title English <span className="text-red-500">*</span>
                                        </button>
                                        <button className={`pe-4 pb-2 font-medium ${activeTab === "id" ? "border-b-2 border-white text-white" : "text-black"}`} onClick={() => setActiveTab("id")}>
                                            Job Title Indonesian
                                        </button>
                                    </div>

                                </div>
                                {activeTab === "en" ? (
                                    <input
                                        type="text"
                                        value={jobTitleEN}
                                        onChange={(e) => setJobTitleEN(e.target.value)}
                                        placeholder="Job Title"
                                        className="border px-4 py-2 rounded-lg w-full"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={jobTitleID}
                                        onChange={(e) => setJobTitleID(e.target.value)}
                                        placeholder="Judul Pekerjaan"
                                        className="border px-4 py-2 rounded-lg w-full"
                                    />
                                )}
                            </div>
                            <div className="grid grid-cols-1 !mt-4 md:grid-cols-2 gap-4">
                                <div>

                                    <label className="mb-1 text-sm">Job Type <span className="text-red-500">*</span></label>
                                    <select value={jobType} onChange={(e) => setJobType(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Select Job Type</option>
                                        {jobTypes.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>

                                </div>

                                <div>
                                    <label className="mb-1 text-sm">Industry Type <span className="text-red-500">*</span></label>

                                    <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Industry Type</option>
                                        {industries.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>

                                </div>

                                <div>

                                    <label className="mb-1 text-sm">Working Type <span className="text-red-500">*</span></label>
                                    <select value={workingType} onChange={(e) => setWorkingType(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Select Working Type</option>
                                        {workingTypes.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="mb-1 text-sm">Degree <span className="text-red-500">*</span></label>

                                    <select value={degree} onChange={(e) => setDegree(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Select Degree</option>
                                        {degrees.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>

                                </div>

                                <div>

                                    <label className="mb-1 text-sm">Experience <span className="text-red-500">*</span></label>

                                    <select value={experience} onChange={(e) => setExperience(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Select Experience</option>
                                        {experiences.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>
                                </div>

                                <div>

                                    <label className="mb-1 text-sm">Experience Level <span className="text-red-500">*</span></label>

                                    <select value={experienceLevel} onChange={(e) => setExperienceLevel(e.target.value)} className="border px-4 py-2 rounded-lg w-full">
                                        <option>Select Experience Level</option>
                                        {experienceLevels.map((item) => <option key={item.name} value={item.id}>{item.name}</option>)}
                                    </select>

                                </div>
                            </div>

                            <div className="!mt-4 ">

                                <label className="text-sm">Location <span className="text-red-500">*</span></label>

                            </div>

                            <div className="grid grid-cols-1 !mt-0 md:grid-cols-3 gap-4">

                                {/* Country */}
                                <select value={countryId} onChange={(e) => setCountryId(e.target.value)} className="border px-4 py-2 rounded-lg">
                                    <option value="">Select Country</option>
                                    {countries.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>

                                {/* State */}
                                <select value={stateId} onChange={(e) => setStateId(e.target.value)} className="border px-4 py-2 rounded-lg" disabled={!countryId}>
                                    <option value="">Select State</option>
                                    {states.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>

                                {/* City */}
                                <select value={cityId} onChange={(e) => setCityId(e.target.value)} className="border px-4 py-2 rounded-lg" disabled={!stateId}>
                                    <option value="">Select City</option>
                                    {cities.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        {/* SALARY */}
                        <div className="mt-6 space-y-2">
                            <p className="font-medium">Salary Information</p>

                            <p className="bg-blue-50 text-blue-700 text-sm p-2 rounded-lg">
                                Ensure the salary range between min. salary and max.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 md:gap-4 items-end">
                                {/* Min Salary */}
                                <div className="flex flex-col">
                                    <label className="mb-1 text-sm">
                                        Min Salary <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formatCurrency(minSalary)}
                                        onChange={handleMinChange}
                                        placeholder="Min Salary"
                                        className="border px-4 py-2 rounded-lg w-full"
                                    />
                                </div>

                                <p className="hidden md:flex items-center justify-center">-</p>

                                {/* Max Salary */}
                                <div className="flex flex-col">
                                    <label className="mb-1 text-sm">
                                        Max Salary <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formatCurrency(maxSalary)}
                                        onChange={handleMaxChange}
                                        placeholder="Max Salary"
                                        className="border px-4 py-2 rounded-lg w-full"
                                    />
                                </div>
                            </div>


                            {/* Undisclosed */}
                            <label className="flex items-center gap-2 mt-2">
                                <input
                                    type="checkbox"
                                    checked={notDisclose}
                                    onChange={handleToggleUndisclosed}
                                />
                                <span>Undisclosed?</span>
                            </label>
                        </div>


                        {/* LANGUAGE TABS */}
                        <div className="flex border-b mb-4 mt-8">
                            <button className={`px-4 py-2 font-medium ${activeTab === "en" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"}`} onClick={() => setActiveTab("en")}>
                                English
                            </button>
                            <button className={`px-4 py-2 font-medium ${activeTab === "id" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"}`} onClick={() => setActiveTab("id")}>
                                Indonesian
                            </button>
                        </div>

                        {/* RESPONSIBILITIES */}
                        <div className="mt-6">
                            <p className="font-medium mb-2">Responsibilities <span className="text-red-500">*</span></p>
                            {activeTab === "en" ? (
                                <TiptapEditor
                                    placeholder="Describe the main responsibilities for this role.
Include daily tasks, team collaboration, and key objectives.
Example:
• Develop and maintain web applications
• Collaborate with designers and backend teams
• Ensure code quality and performance"
                                    value={responsibilitiesEN}
                                    onChange={setResponsibilitiesEN}
                                />
                            ) : (
                                <TiptapEditor
                                    placeholder="Describe the main responsibilities for this role.
Include daily tasks, team collaboration, and key objectives.
Example:
• Develop and maintain web applications
• Collaborate with designers and backend teams
• Ensure code quality and performance"
                                    value={responsibilitiesID}
                                    onChange={setResponsibilitiesID}
                                />
                            )}
                        </div>

                        <div className="mt-6">
                            <p className="font-medium mb-2">Requirements <span className="text-red-500">*</span></p>
                            {activeTab === "en" ? (
                                <TiptapEditor // <- key unik biar remount
                                    placeholder="List the qualifications and skills required for this role.
Include education, experience, and technical skills.
Example:
• Bachelor's degree in related field
• 2+ years of relevant experience
• Proficiency in required tools or technologies"
                                    value={requirementsEN}
                                    onChange={setRequirementsEN}
                                />
                            ) : (
                                <TiptapEditor
                                    placeholder="List the qualifications and skills required for this role.
Include education, experience, and technical skills.
Example:
• Bachelor's degree in related field
• 2+ years of relevant experience
• Proficiency in required tools or technologies"
                                    value={requirementsID}
                                    onChange={setRequirementsID}
                                />
                            )}
                        </div>



                        {/* BENEFITS */}
                        <div className="mt-6">
                            <p className="font-medium mb-2">Benefits <span className="text-red-500">*</span></p>
                            <TagSelectInput
                                value={benefits}
                                onChange={setBenefits}
                                options={benefitOptions}
                            />

                        </div>

                        {/* SKILLS */}
                        <div className="mt-6">
                            <p className="font-medium mb-2">Skills <span className="text-red-500">*</span></p>
                            <TagSelectInput
                                value={skills}
                                onChange={setSkills}
                                options={skillOptions}
                            />
                        </div>

                        {/* UPDATE BUTTON */}
                        <div className="flex justify-end mt-8">
                            <Button
                                textClass="text-white"
                                onClick={handleSubmit}
                                disabled={!isFormValid || isSubmitting}
                                className={`flex items-center justify-center gap-2 ${!isFormValid || isSubmitting
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                        Submitting...
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </Button>

                        </div>
                    </SectionBox>
                </div>
            </main>
            <Footer />
        </>
    );
}
